import React from 'react';
import type { MapEditorTool } from '../../types/warehouse';
import {
  MousePointer2,
  Hand,
  Square,
  Grid3X3,
  Grid,
  Tag,
  Layers,
  Maximize2,
  RotateCcw,
  Magnet,
  Ruler,
  AlertTriangle,
  Keyboard,
} from 'lucide-react';

interface MapToolbarProps {
  activeTool: MapEditorTool;
  showGrid: boolean;
  showZoneLabels: boolean;
  showRackLabels: boolean;
  gridSize: number;
  zoneOpacity: number;
  snapToGrid?: boolean;
  showRulers?: boolean;
  showCollisionWarnings?: boolean;
  canCreateZone?: boolean;
  canCreateRack?: boolean;
  onToolChange: (tool: MapEditorTool) => void;
  onShowGridChange: (show: boolean) => void;
  onShowZoneLabelsChange: (show: boolean) => void;
  onShowRackLabelsChange: (show: boolean) => void;
  onGridSizeChange: (size: number) => void;
  onZoneOpacityChange: (opacity: number) => void;
  onSnapToGridChange?: (enabled: boolean) => void;
  onShowRulersChange?: (show: boolean) => void;
  onShowCollisionWarningsChange?: (show: boolean) => void;
  onFitToContent: () => void;
  onResetView: () => void;
  onShowKeyboardShortcuts?: () => void;
}

const GRID_SIZES = [5, 10, 20, 25, 50];

export const MapToolbar: React.FC<MapToolbarProps> = ({
  activeTool,
  showGrid,
  showZoneLabels,
  showRackLabels,
  gridSize,
  zoneOpacity,
  snapToGrid = true,
  showRulers = true,
  showCollisionWarnings = true,
  canCreateZone = false,
  canCreateRack = false,
  onToolChange,
  onShowGridChange,
  onShowZoneLabelsChange,
  onShowRackLabelsChange,
  onGridSizeChange,
  onZoneOpacityChange,
  onSnapToGridChange,
  onShowRulersChange,
  onShowCollisionWarningsChange,
  onFitToContent,
  onResetView,
  onShowKeyboardShortcuts,
}) => {
  const tools: { id: MapEditorTool; label: string; icon: React.FC<{ className?: string }>; shortcut: string; disabled: boolean }[] = [
    { id: 'select', label: 'Select', icon: MousePointer2, shortcut: 'V', disabled: false },
    { id: 'pan', label: 'Pan', icon: Hand, shortcut: 'H', disabled: false },
    { id: 'zone', label: 'Zone', icon: Square, shortcut: 'Z', disabled: !canCreateZone },
    { id: 'rack', label: 'Rack', icon: Grid3X3, shortcut: 'R', disabled: !canCreateRack },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2">
        <div className="flex flex-col gap-1">
          {tools.map((tool) => {
            const Icon = tool.icon;
            const isActive = activeTool === tool.id;

            return (
              <button
                key={tool.id}
                onClick={() => !tool.disabled && onToolChange(tool.id)}
                disabled={tool.disabled}
                className={`relative group flex items-center justify-center w-10 h-10 rounded-lg transition-all ${
                  isActive
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : tool.disabled
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                title={`${tool.label} (${tool.shortcut})${tool.disabled ? ' - Not available' : ''}`}
              >
                <Icon className="h-5 w-5" />

                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  {tool.label}
                  <span className="ml-2 text-gray-400">{tool.shortcut}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2">
        <div className="flex flex-col gap-1">
          <ToggleButton
            active={showGrid}
            onClick={() => onShowGridChange(!showGrid)}
            icon={Grid}
            label="Grid"
            shortcut="G"
          />
          <ToggleButton
            active={showZoneLabels}
            onClick={() => onShowZoneLabelsChange(!showZoneLabels)}
            icon={Tag}
            label="Zone Labels"
            shortcut="L"
          />
          <ToggleButton
            active={showRackLabels}
            onClick={() => onShowRackLabelsChange(!showRackLabels)}
            icon={Layers}
            label="Rack Labels"
            shortcut="K"
          />
          {onSnapToGridChange && (
            <ToggleButton
              active={snapToGrid}
              onClick={() => onSnapToGridChange(!snapToGrid)}
              icon={Magnet}
              label="Snap to Grid"
              shortcut="S"
            />
          )}
          {onShowRulersChange && (
            <ToggleButton
              active={showRulers}
              onClick={() => onShowRulersChange(!showRulers)}
              icon={Ruler}
              label="Rulers"
              shortcut=""
            />
          )}
          {onShowCollisionWarningsChange && (
            <ToggleButton
              active={showCollisionWarnings}
              onClick={() => onShowCollisionWarningsChange(!showCollisionWarnings)}
              icon={AlertTriangle}
              label="Collision Warnings"
              shortcut=""
            />
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2">
        <div className="flex flex-col gap-1">
          <button
            onClick={onFitToContent}
            className="group flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors relative"
            title="Fit to content"
          >
            <Maximize2 className="h-5 w-5" />
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              Fit to Content
            </div>
          </button>
          <button
            onClick={onResetView}
            className="group flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors relative"
            title="Reset view"
          >
            <RotateCcw className="h-5 w-5" />
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              Reset View
            </div>
          </button>
          {onShowKeyboardShortcuts && (
            <button
              onClick={onShowKeyboardShortcuts}
              className="group flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors relative"
              title="Keyboard shortcuts"
            >
              <Keyboard className="h-5 w-5" />
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                Shortcuts
                <span className="ml-2 text-gray-400">?</span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface ToggleButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.FC<{ className?: string }>;
  label: string;
  shortcut: string;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  active,
  onClick,
  icon: Icon,
  label,
  shortcut,
}) => (
  <button
    onClick={onClick}
    className={`relative group flex items-center justify-center w-10 h-10 rounded-lg transition-all ${
      active
        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
    }`}
    title={`${label}${shortcut ? ` (${shortcut})` : ''}`}
  >
    <Icon className="h-5 w-5" />
    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
      {label}
      {shortcut && <span className="ml-2 text-gray-400">{shortcut}</span>}
    </div>
  </button>
);

interface MapSettingsPanelProps {
  showGrid: boolean;
  gridSize: number;
  zoneOpacity: number;
  snapToGrid?: boolean;
  showRulers?: boolean;
  showCollisionWarnings?: boolean;
  onShowGridChange: (show: boolean) => void;
  onGridSizeChange: (size: number) => void;
  onZoneOpacityChange: (opacity: number) => void;
  onSnapToGridChange?: (enabled: boolean) => void;
  onShowRulersChange?: (show: boolean) => void;
  onShowCollisionWarningsChange?: (show: boolean) => void;
}

export const MapSettingsPanel: React.FC<MapSettingsPanelProps> = ({
  showGrid,
  gridSize,
  zoneOpacity,
  snapToGrid = true,
  showRulers = true,
  showCollisionWarnings = true,
  onShowGridChange,
  onGridSizeChange,
  onZoneOpacityChange,
  onSnapToGridChange,
  onShowRulersChange,
  onShowCollisionWarningsChange,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4 min-w-[240px]">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <Grid className="h-4 w-4" />
        Display Settings
      </h3>

      <div className="space-y-4">
        <SettingsToggle
          label="Show Grid"
          value={showGrid}
          onChange={onShowGridChange}
        />

        {showGrid && (
          <div className="space-y-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
            <label className="text-xs text-gray-500 dark:text-gray-400">Grid Size</label>
            <div className="flex gap-1">
              {GRID_SIZES.map((size) => (
                <button
                  key={size}
                  onClick={() => onGridSizeChange(size)}
                  className={`flex-1 py-1 text-xs rounded transition-colors ${
                    gridSize === size
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}

        {onSnapToGridChange && (
          <SettingsToggle
            label="Snap to Grid"
            value={snapToGrid}
            onChange={onSnapToGridChange}
          />
        )}

        {onShowRulersChange && (
          <SettingsToggle
            label="Show Rulers"
            value={showRulers}
            onChange={onShowRulersChange}
          />
        )}

        {onShowCollisionWarningsChange && (
          <SettingsToggle
            label="Collision Warnings"
            value={showCollisionWarnings}
            onChange={onShowCollisionWarningsChange}
          />
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 dark:text-gray-400">Zone Opacity</label>
            <span className="text-xs text-gray-500 dark:text-gray-500 tabular-nums">
              {Math.round(zoneOpacity * 100)}%
            </span>
          </div>
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.1}
            value={zoneOpacity}
            onChange={(e) => onZoneOpacityChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>
      </div>
    </div>
  );
};

interface SettingsToggleProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

const SettingsToggle: React.FC<SettingsToggleProps> = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
    <button
      onClick={() => onChange(!value)}
      className={`relative w-10 h-6 rounded-full transition-colors ${
        value ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow ${
          value ? 'translate-x-4' : ''
        }`}
      />
    </button>
  </div>
);
